import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { login, logout, getUser, googleLogin } from '../lib/auth';
import { tokenStorage } from '../services/api';
import { useCurrentUser, AuthContext } from '../hooks/useAuth';
import { navigateToLogin } from '../lib/navigation';
import { setSentryUser } from '../lib/sentry';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Only call useCurrentUser if we have a token
  const hasToken = !!tokenStorage.getAccessToken();
  const { data: userData, isLoading, error } = useCurrentUser({
    enabled: hasToken, // Only run query if token exists
    retry: false, // Don't retry on error
    refetchOnWindowFocus: false // Don't refetch on window focus
  });

  useEffect(() => {
    // Check if user is already logged in
    const token = tokenStorage.getAccessToken();
    if (token) {
      // The useCurrentUser hook will handle the API call
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
        setSentryUser({ id: userData.id, role: userData.role });
        // Store user role in localStorage for route loaders
        if (userData.role) {
          localStorage.setItem('user', JSON.stringify({ role: userData.role }));
        }
        setLoading(false);
      } else if (userData === null) {
        // Token is invalid, clear it silently
        tokenStorage.clearTokens();
        setUser(null);
        setIsAuthenticated(false);
        setSentryUser(null);
        setLoading(false);
      } else if (error) {
        // Check if it's a 401 error (token expired, revoked, or invalid)
        const is401Error = error.message && (
          error.message.includes('Token has been revoked') ||
          error.message.includes('Token has expired') ||
          error.message.includes('401') ||
          error.message.includes('Unauthorized') ||
          error.message.includes('Authentication required')
        );

        if (is401Error) {
          tokenStorage.clearTokens();
          setUser(null);
          setIsAuthenticated(false);
          setSentryUser(null);
          setLoading(false);
          navigateToLogin();
        } else {
          tokenStorage.clearTokens();
          setUser(null);
          setIsAuthenticated(false);
          setSentryUser(null);
          setLoading(false);
        }
      } else if (isLoading) {
        setLoading(true);
      }
    } else {
      setLoading(false);
    }
  }, [userData, error, isLoading]);

  const handleLogin = async (email, password) => {
    // console.log('🎯 AuthContext.handleLogin called with:', { email, password: '***' });

    try {
      // console.log('📞 Calling login function from auth.ts...');
      const result = await login(email, password);
      // console.log('📞 login function returned:', result);

      if (result.success) {
        // console.log('✅ Login successful, updating state...');
        // console.log('👤 Setting user:', result.user);

        // Clear all cached queries to prevent showing previous user's data
        queryClient.clear();

        setUser(result.user);
        setIsAuthenticated(true);
        setSentryUser({ id: result.user.id, role: result.user.role });
        // Store user role in localStorage for route loaders
        if (result.user?.role) {
          localStorage.setItem('user', JSON.stringify({ role: result.user.role }));
        }
        return { success: true };
      } else {
        // console.log('❌ Login failed:', result.error);
        return result;
      }
    } catch (error) {
      // console.log('💥 AuthContext login error:', error);
      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  };

  const handleLogout = () => {
    // Clear all cached queries before logout to prevent data leakage
    queryClient.clear();

    logout();
    setUser(null);
    setIsAuthenticated(false);
    setSentryUser(null);
    // Clear user data from localStorage
    localStorage.removeItem('user');
    // Navigate to login page immediately
    navigateToLogin();
  };

  const handleGoogleLogin = async (credential, preferredLanguage = null) => {
    try {
      const result = await googleLogin(credential, preferredLanguage);

      if (result.success) {
        // Clear all cached queries to prevent showing previous user's data
        queryClient.clear();

        setUser(result.user);
        setIsAuthenticated(true);
        setSentryUser({ id: result.user.id, role: result.user.role });
        // Store user role in localStorage for route loaders
        if (result.user?.role) {
          localStorage.setItem('user', JSON.stringify({ role: result.user.role }));
        }
        return { success: true };
      } else {
        return result;
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Google login failed'
      };
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login: handleLogin,
    googleLogin: handleGoogleLogin,
    logout: handleLogout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
